import { Injectable } from "@angular/core";
import {
    HttpClient,
    HttpErrorResponse,
    HttpResponse,
    HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Department } from "../model/departments";
import { Labeler } from "../model/labeler";

import { PALETTE } from "../model/palette";

@Injectable({
    providedIn: "root",
})
export class DepartmentsService implements Labeler {
    private apiUrl: string;
    private byIdUrl: string;
    public _currentDepartments: BehaviorSubject<Department[]>;
    public selectedDepartment: BehaviorSubject<Department | null>;

    constructor(
        private http: HttpClient,
        private env: EnvironmentHelper,
        private router: Router,
        private auth: AuthService
    ) {
        this._currentDepartments = new BehaviorSubject<Department[]>([]);
        this.selectedDepartment = new BehaviorSubject<Department | null>(null);
        this.apiUrl = `${env.apiBaseUrl}department/`;
        this.byIdUrl = `${env.apiBaseUrl}department/by-id/`;
    }

    public departments(token: string): Observable<Messages> {
        return new Observable<Messages>((observer) => {
            this.http
                .get<any>(this.apiUrl, {
                    headers: { Authorization: "Bearer " + token },
                    observe: "response",
                })
                .subscribe(
                    (response) => {
                        this.receiveDepartments(response, observer);
                    },
                    (error) => {
                        this.receiveDepartments(error, observer);
                    }
                );
        });
    }

    public createDepartment(
        token: string,
        department: Department
    ): Observable<Messages> {
        return new Observable<Messages>((observer) => {
            this.http
                .post<any>(this.apiUrl, department, {
                    headers: {
                        Authorization: "Bearer " + token,
                        "Content-type": "application/json",
                    },
                    observe: "response",
                })
                .subscribe(
                    (response) => {
                        this.receiveDepartment(response, observer);
                    },
                    (error) => {
                        this.receiveDepartment(error, observer);
                    }
                );
        });
    }

    public updateDepartments(
        token: string,
        departments: Department
    ): Observable<Messages> {
        const url = `${this.apiUrl}by-id/${departments.id}`;

        return new Observable<Messages>((observer) => {
            if (!departments.id) {
                observer.next(
                    Messages.from({ departments: ["departments.id.required"] })
                );
                return;
            }

            this.http
                .put<any>(url, departments, {
                    headers: {
                        Authorization: "Bearer " + token,
                        "Content-Type": "application/json",
                    },
                    observe: "response",
                })
                .subscribe(
                    (response) => this.receiveDepartment(response, observer),
                    (error) => this.receiveDepartment(error, observer)
                );
        });
    }

    public deleteDepartment(
        token: string,
        departmentId: number
    ): Observable<Messages> {
        const url = `${this.apiUrl}by-id/${departmentId}`;
        return new Observable<Messages>((observer) => {
            this.http
                .delete<any>(url, {
                    headers: {
                        Authorization: "Bearer " + token,
                        "Content-Type": "application/json",
                    },
                    observe: "response",
                })
                .subscribe(
                    (response) => this.receiveDepartment(response, observer),
                    (error) => this.receiveDepartment(error, observer)
                );
        });
    }
    public getDepartmentsById(
        token: string,
        departmentsId: number
    ): Observable<Messages> {
        return new Observable<Messages>((observer) => {
            this.http
                .get<any>(`${this.byIdUrl}${departmentsId}`, {
                    headers: {
                        Authorization: "Bearer " + token,
                        "Content-type": "application/json",
                    },
                    observe: "response",
                })
                .subscribe(
                    (response) => this.receiveDepartment(response, observer),
                    (error) => this.receiveDepartment(error, observer)
                );
        });
    }

    private receiveDepartments(
        response: HttpResponseBase,
        observer: Subscriber<Messages>
    ) {
        if (response.status === 200) {
            const data = (response as HttpResponse<Department[]>)?.body;
            if (!data) {
                observer.next(Messages.from({ departments: ["departments.unknown"] }));
                return;
            }
            this._currentDepartments.next(data);
            observer.next(Messages.EMPTY);
        } else if (response.status === 401) {
            observer.next(Messages.from({ account: ["login.not.authorized"] }));
        } else {
            observer.next(
                Messages.orElse(
                    (response as HttpErrorResponse).error,
                    "departments",
                    "departments.incomplete"
                )
            );
        }
    }

    private receiveDepartment(
        response: HttpResponseBase,
        observer: Subscriber<Messages>
    ) {
        if (response.status === 200) {
            const data = (response as HttpResponse<Department>)?.body;
            if (!data) {
                observer.next(Messages.from({ department: ["department.unknown"] }));
                return;
            }
            const current = this._currentDepartments.value;

            const updated = current.some(d => d.id === data.id)
                ? current.map(d => d.id === data.id ? data : d)
                : [...current, data];

            this._currentDepartments.next(updated);
            observer.next(Messages.EMPTY);

        } else if (response.status === 204) {
            observer.next(Messages.EMPTY);
        } else if (
            response.status === 400 ||
            response.status === 401 ||
            response.status === 409 ||
            response.status === 500
        ) {
            observer.next(
                Messages.orElse(
                    (response as HttpErrorResponse).error,
                    "department",
                    "department.load.error."
                )
            );
        } else {
            observer.next(
                Messages.orElse(
                    (response as HttpErrorResponse).error,
                    "general",
                    "an.unexpected.error.has.occurred."
                )
            );
        }
    }

    public selectFromRouter() {
        const segments = this.getLastSuccessfulNavigationSegments();

        if (segments && segments.length > 0) {
            const departmentId = segments[segments.length - 1];
            const token = this.auth.token;

            this.getDepartmentsById(token, Number(departmentId)).subscribe(
                (messages) => { }
            );
        } else {
            const emptyDepartment = new Department(0, "");
            this.select(emptyDepartment);
        }
    }

    private getLastSuccessfulNavigationSegments(): string[] | null {
        const segments =
            this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
                .segments;
        return segments ? segments.map((segment) => segment.path) : null;
    }

    public select(department: Department) {
        this.selectedDepartment.next(department);
    }

    public get selectedDepartmentObservable(): Observable<Department | null> {
        return this.selectedDepartment.asObservable();
    }
    public get departmentsObservable(): Observable<Department[]> {
        return this._currentDepartments;
    }

    public get departmentsData(): Department[] {
        return this._currentDepartments.value;
    }

    public get currentDepartment(): Department {
        const value = this.selectedDepartment.value;
        return value || Department.EMPTY;
    }

    public labelForId(id: number): string {
        let label = "-";
        if (this.departmentsData?.length > 0) {
            const found = this.departmentsData.filter(
                (department) => id === department.id
            );
            if (found.length > 0) {
                label = found[0].name;
            }
        }
        return label;
    }
    public makeLabel(id: number): Observable<string> {
        const observable = new BehaviorSubject("");

        if (this.departmentsData?.length > 0) {
            const label = this.labelForId(id);
            observable.next(label);
        } else {
            this.departments(this.auth.token).subscribe((m) => {
                observable.next(this.labelForId(id));
            });
        }
        return observable;
    }

    public makeColor(id: number): Observable<string> {
        return new BehaviorSubject(PALETTE.ink);
    }
    /**
     * The colour of the department category, which the pills mix their border,
     * fill and text out of.
     *
     * Zonnig geel, from the customer's palette (#173). Yellow goes muddy when it
     * is darkened by formula, which is why the pills do not mix their text out
     * of this: they take the --dep-* tokens in styles.css, where the text is
     * pushed darker than the other categories get.
     */
    public makeBackgroundColor(id: number): Observable<string> {
        return new BehaviorSubject(PALETTE.zonnigGeel);
    }
    public makeImportance(id: number): Observable<number> {
        return new BehaviorSubject(1);
    }
}
